import { type CreateActiveViewInput, type CreateActiveViewMutation } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createActiveViewMutationDocument } from "./active-view.gql";
import { EVENT_ACTIVE_VIEW_CREATED, trackActiveViewEvent } from "./tracking";

export type CreatedActiveView = CreateActiveViewMutation["createActiveView"];

export interface UseCreateActiveViewProps {
  onCompleted?: (data: CreatedActiveView) => void;
  onError?: (e: Error) => void;
}

export const useCreateActiveView = ({ onCompleted, onError }: UseCreateActiveViewProps = {}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: CreateActiveViewInput) => {
      const client = createOperationClient("CreateActiveView");
      const data = await client.request(createActiveViewMutationDocument, { input });
      return data.createActiveView;
    },
    onSuccess: (newActiveView) => {
      trackActiveViewEvent(EVENT_ACTIVE_VIEW_CREATED, newActiveView);

      // Invalidate active view list to refetch with new data
      queryClient.invalidateQueries({
        queryKey: queryKeys.activeViews.list(newActiveView.workspaceId),
      });

      // Call the onCompleted callback
      if (onCompleted) {
        onCompleted(newActiveView);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    createActiveView: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
